/**
 * The states and union territories a customer's address can name.
 *
 * Keyed by GST STATE CODE rather than by name, because that two-digit code is what the rest of
 * the product already speaks: `stateCodeSchema` validates it, an invoice's place of supply is
 * one, and whether a bill carries IGST or CGST+SGST is decided by comparing two of them. A list
 * keyed by name would have to be mapped back to a code at every one of those points, and a
 * shop that typed "Tamilnadu" into a free-text box would produce a bill nobody could compute.
 *
 * The codes are the official ones, so 25 (Daman and Diu) is absent: it was merged into 26 in
 * 2020 and a shop selecting it today would be filing against a state that no longer issues
 * registrations. 97 (Other Territory) and 99 (Centre Jurisdiction) are absent for the opposite
 * reason — they are real, but a walk-in customer does not live in either, and offering them on
 * a customer's address would only invite a mis-click.
 */
export interface IndianState {
  /** The two-digit GST state code. Matches `stateCodeSchema`. */
  readonly code: string;
  readonly name: string;
}

export const INDIAN_STATES: readonly IndianState[] = Object.freeze([
  { code: '01', name: 'Jammu and Kashmir' },
  { code: '02', name: 'Himachal Pradesh' },
  { code: '03', name: 'Punjab' },
  { code: '04', name: 'Chandigarh' },
  { code: '05', name: 'Uttarakhand' },
  { code: '06', name: 'Haryana' },
  { code: '07', name: 'Delhi' },
  { code: '08', name: 'Rajasthan' },
  { code: '09', name: 'Uttar Pradesh' },
  { code: '10', name: 'Bihar' },
  { code: '11', name: 'Sikkim' },
  { code: '12', name: 'Arunachal Pradesh' },
  { code: '13', name: 'Nagaland' },
  { code: '14', name: 'Manipur' },
  { code: '15', name: 'Mizoram' },
  { code: '16', name: 'Tripura' },
  { code: '17', name: 'Meghalaya' },
  { code: '18', name: 'Assam' },
  { code: '19', name: 'West Bengal' },
  { code: '20', name: 'Jharkhand' },
  { code: '21', name: 'Odisha' },
  { code: '22', name: 'Chhattisgarh' },
  { code: '23', name: 'Madhya Pradesh' },
  { code: '24', name: 'Gujarat' },
  { code: '26', name: 'Dadra and Nagar Haveli and Daman and Diu' },
  { code: '27', name: 'Maharashtra' },
  { code: '29', name: 'Karnataka' },
  { code: '30', name: 'Goa' },
  { code: '31', name: 'Lakshadweep' },
  { code: '32', name: 'Kerala' },
  { code: '33', name: 'Tamil Nadu' },
  { code: '34', name: 'Puducherry' },
  { code: '35', name: 'Andaman and Nicobar Islands' },
  { code: '36', name: 'Telangana' },
  { code: '37', name: 'Andhra Pradesh' },
  { code: '38', name: 'Ladakh' },
]);

const BY_CODE: ReadonlyMap<string, IndianState> = new Map(
  INDIAN_STATES.map((state) => [state.code, state]),
);

/** The state's name, or null for a code that is not one of ours. Never throws — a stored code
 *  from before this list existed must render as nothing rather than break the screen. */
export function indianStateName(code: string | null | undefined): string | null {
  if (!code) return null;
  return BY_CODE.get(code)?.name ?? null;
}
